import './CaremapprSwitch.scss';
import React, { useEffect, useState } from 'react';

export interface CaremapprSwitchProps {
  checked?: boolean; // controlled
  defaultChecked?: boolean; // uncontrolled initial state
  onChange?: (
    checked: boolean,
    event: React.ChangeEvent<HTMLInputElement>,
  ) => void;
  disabled?: boolean;
  label?: string;
  id?: string;
  className?: string;
  style?: React.CSSProperties;
  size?: 'sm' | 'md' | 'lg';
}

export const CaremapprSwitch: React.FC<CaremapprSwitchProps> = ({
  checked,
  defaultChecked = false,
  onChange,
  disabled = false,
  label,
  id,
  className,
  style,
  size = 'md',
}) => {
  const [internalChecked, setInternalChecked] =
    useState<boolean>(defaultChecked);
  const isControlled = typeof checked === 'boolean';
  const currentChecked = isControlled ? (checked as boolean) : internalChecked;

  useEffect(() => {
    if (isControlled) {
      setInternalChecked(checked as boolean);
    }
  }, [checked, isControlled]);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const next = e.target.checked;
    if (!isControlled) setInternalChecked(next);
    onChange?.(next, e);
  };

  const sizeClass =
    size === 'sm' ? 'form-switch-sm' : size === 'lg' ? 'form-switch-lg' : '';
  const baseClass = `form-check form-switch ${sizeClass}`.trim();
  const combinedClass = [baseClass, className].filter(Boolean).join(' ');
  const switchId =
    id ?? `caremappr-switch-${Math.random().toString(36).slice(2, 8)}`;

  return (
    <div className={combinedClass} style={style}>
      <input
        className="form-check-input"
        type="checkbox"
        id={switchId}
        checked={currentChecked}
        onChange={handleChange}
        disabled={disabled}
        role="switch"
        aria-checked={currentChecked}
      />
      {label && (
        <label className="form-check-label" htmlFor={switchId}>
          {label}
        </label>
      )}
    </div>
  );
};

// Legacy compatibility (optional): keep the old named export as alias
export const SwitchComponent = CaremapprSwitch;
